import React, { useMemo, useEffect } from 'react';

import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

import {
  FormControl,
  TextField,
  FormHelperText,
  Box,
  Button,
  Chip,
  Typography,
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import Autocomplete from '@material-ui/lab/Autocomplete';

import { useGetTenants } from '../../hooks';
import { PimStatusChip } from './PimStatusChip';
import { useStyles } from './styles';
import { KaapTenant } from './types';
import { serializeTenants } from './utils';

export const KaapTenantPicker = (
  props: FieldExtensionComponentProps<string | KaapTenant>,
) => {
  const {
    onChange,
    schema: { title = '', description = '', type = 'string' },
    required,
    rawErrors,
    formData,
    idSchema,
  } = props;
  const classes = useStyles();

  const [{ value: tenants, loading }, fetchTenants] = useGetTenants();

  useEffect(() => {
    void fetchTenants();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const tenantOptions = useMemo(
    () => tenants?.map(tenant => serializeTenants(tenant)) || [],
    [tenants],
  );

  const selectedOption = useMemo(() => {
    if (!formData) return null;

    if (typeof formData === 'string') {
      return tenantOptions.find(option => option.id === formData) || null;
    }

    if (typeof formData === 'object' && formData && 'id' in formData) {
      return (
        tenantOptions.find(option => option.id === (formData as any).id) ||
        (formData as KaapTenant)
      );
    }

    return null;
  }, [formData, tenantOptions]);

  useEffect(() => {
    if (
      selectedOption &&
      typeof formData === 'object' &&
      formData &&
      'id' in formData &&
      selectedOption.id === (formData as KaapTenant).id &&
      selectedOption.pimGroupId !== (formData as KaapTenant).pimGroupId
    ) {
      if (type === 'object') {
        onChange(selectedOption);
      } else {
        onChange(selectedOption.id);
      }
    }
  }, [selectedOption?.id, selectedOption?.pimGroupId]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSelect = (_: any, value: KaapTenant | null) => {
    if (!value) {
      onChange('');
      return;
    }

    if (type === 'object') {
      onChange(value as KaapTenant);
    } else {
      onChange(value.id);
    }
  };

  const showRefreshButton =
    selectedOption?.pimEnabled && !selectedOption?.pimGroupId;

  return (
    <Box className={classes.flexContainer}>
      <Box className={classes.inputContainer}>
        <FormControl
          fullWidth
          required={required}
          error={rawErrors?.length > 0 && !formData}
        >
          <Autocomplete
            size="small"
            id={idSchema?.$id}
            value={selectedOption}
            onChange={onSelect}
            options={tenantOptions}
            getOptionLabel={option => option.name}
            loading={loading}
            renderInput={params => (
              <TextField
                {...params}
                label={title}
                margin="none"
                color="secondary"
                helperText={description}
                variant="outlined"
                required={required}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      <PimStatusChip
                        pimEnabled={selectedOption?.pimEnabled}
                        pimGroupId={selectedOption?.pimGroupId}
                        loading={loading}
                      />
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                error={Boolean(rawErrors?.length > 0)}
              />
            )}
            renderOption={option => (
              <Box>
                <Box display="flex" alignItems="center">
                  <Typography variant="body2" noWrap>
                    {option.name}
                  </Typography>
                  {option.pimEnabled && (
                    <Chip
                      label="PIM enabled"
                      size="small"
                      variant="outlined"
                      className={classes.pimSecondaryChip}
                    />
                  )}
                </Box>
                <Typography variant="caption" noWrap color="textSecondary">
                  Cluster ID: {option.clusterId}
                </Typography>
              </Box>
            )}
            noOptionsText="No tenants found"
            loadingText="Loading tenants..."
          />
          {rawErrors?.map((err, index) => (
            <FormHelperText key={index} error>
              {err}
            </FormHelperText>
          ))}
        </FormControl>
      </Box>
      {showRefreshButton && (
        <Box marginBottom={2}>
          <Button
            startIcon={<RefreshIcon />}
            color="secondary"
            variant="outlined"
            onClick={fetchTenants}
            disabled={loading}
            className={classes.checkAgainButton}
          >
            {loading ? 'Checking...' : 'Check again'}
          </Button>
        </Box>
      )}
    </Box>
  );
};
